import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonBase } from '@mui/material';
import { css, styled } from '@mui/material/styles';

import ErrorCorrectionLevelIcon from '~/assets/icons/design-qr/error-correction.svg?react';
import LevelH from '~/assets/icons/LEVEL_H.svg?react';
import LevelL from '~/assets/icons/LEVEL_L.svg?react';
import LevelM from '~/assets/icons/LEVEL_M.svg?react';
import LevelQ from '~/assets/icons/LEVEL_Q.svg?react';
import DesignStepBlockHeaderLayout from '~/components/QRCodeDesign/DesignStepBlockHeaderLayout';
import Accordion from '~/components/ui/Accordion/Accordion';
import { Text } from '~/components/ui/text';
import { DEFAULT_QR_CODE_STYLES, DESKTOP_MIN_WIDTH } from '~/constants/common';
import { COLORS } from '~/styles/colors';
import { QrCodeCorrectionLevel } from '~/types/qrCode';

const LEVEL_MAP = {
  Q: { Icon: LevelQ, percent: 25 },
  H: { Icon: LevelH, percent: 30 },
  M: { Icon: LevelM, percent: 15 },
  L: { Icon: LevelL, percent: 7 },
};

type QRCodeCorrectionLevelProps = {
  levels: QrCodeCorrectionLevel[];
  defaultSelectedLevel?: QrCodeCorrectionLevel;
  defaultExpanded?: boolean;
  onSelectLevel: (level: QrCodeCorrectionLevel) => void;
};

export default function QRCodeCorrectionLevel({
  levels,
  onSelectLevel,
  defaultSelectedLevel,
  defaultExpanded = false,
}: QRCodeCorrectionLevelProps) {
  const { t } = useTranslation();

  const styles = useStyles();

  const [selectedLevel, setSelectedLevel] = useState(
    defaultSelectedLevel || DEFAULT_QR_CODE_STYLES.errorCorrectionLevel,
  );

  const handleChangeSelectedLevel = (level: QrCodeCorrectionLevel) => {
    setSelectedLevel(level);
    onSelectLevel(level);
  };

  return (
    <Accordion
      sx={{ maxWidth: '100%' }}
      variant="medium"
      defaultExpanded={defaultExpanded}
      summary={
        <DesignStepBlockHeaderLayout
          Icon={ErrorCorrectionLevelIcon}
          title={t('thirdStep:correctionLevel')}
          description={t('builder:designStep.errorCorrectionLevel.description')}
        />
      }
      details={
        <>
          <StyledLevelsContainer>
            {levels?.map((level) => {
              const Icon = LEVEL_MAP[level].Icon;
              const isSelected = selectedLevel === level;

              return (
                <ButtonBase
                  key={level}
                  onClick={() => handleChangeSelectedLevel(level)}
                  sx={styles.level}
                  className={isSelected ? 'selected' : undefined}
                >
                  <Icon />
                  <StyledLevelLabel variant="inputVerySmall" $isSelected={isSelected}>
                    {t('thirdStep:level')}: {level} <span>{LEVEL_MAP[level].percent}%</span>
                  </StyledLevelLabel>
                </ButtonBase>
              );
            })}
          </StyledLevelsContainer>
          <Text
            variant="input15"
            sx={{
              marginTop: '20px',
              backgroundColor: COLORS.grey500,
              border: `1px solid ${COLORS.grey200}`,
              borderRadius: '20px',
              padding: '12px 16px',
              display: 'flex',
              gap: '8px',
            }}
          >
            ℹ️
            <span>{t('builder:designStep.errorCorrectionLevel.infoText')}</span>
          </Text>
        </>
      }
    />
  );
}

function useStyles() {
  return {
    level: css`
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 12px !important;
      border-radius: 16px;
      background-color: ${COLORS.transparent};
      border: none;
      cursor: pointer;

      &.selected {
        background-color: ${COLORS.blue50};

        svg {
          background-color: #e6f3fd !important;

          rect {
            fill: #2196f3;
          }
        }
      }

      svg {
        width: 84px !important;
        height: 86px !important;
      }
    `,
  };
}

export const StyledLevelsContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
      flexWrap: 'nowrap',
      overflow: 'auto',
      overscrollBehavior: 'contain',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
  };
});

export const StyledLevelLabel = styled(Text)<{ $isSelected: boolean }>(({ $isSelected }) => {
  return {
    color: $isSelected ? COLORS.primary : COLORS.textMain,
    display: 'flex',
    justifyContent: 'space-between',
  };
});
